import React, { useState, useEffect, useContext } from "react";
import { makeStyles } from "@material-ui/core/styles";
import Grid from "@material-ui/core/Grid";
import Paper from "@material-ui/core/Paper";
import { Button, Box, TextField, FormControl, Select, MenuItem, Typography } from "@material-ui/core";
import MainContainer from "../../../components/MainContainer";
import { headerData } from "../../../data/modules/dashboard-header";
import { userDetailContext } from "../../../App";
import ImmobilisationService from "../../../services/ImmobilisationService";
import PlanningService from "../../../services/PlanningService";
import LoadingSimpleBackdrop from "../../tools/LoadingSimpleBackdrop";
import ServiceService from "../../../services/ServiceService";
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import AssignmentIcon from '@mui/icons-material/Assignment';
import SearchIconButton from '@mui/icons-material/Search';
import ExcelJS from 'exceljs';
import { saveAs } from 'file-saver';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import TimeTrackingService from "../../../services/TimeTrackingService";

// --- Utilitaires de date ---
const getFirstDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  return `${year}-${month}-01`;
};

const getLastDayOfMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth() + 1;
  const lastDay = new Date(year, month, 0).getDate();
  return `${year}-${String(month).padStart(2, '0')}-${String(lastDay).padStart(2, '0')}`;
};

const monthNames = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre'
];

const EXCEL_HEADERS = [
  'N°', 'Direction', 'Service', 'Fonction',
  'Noms et prénoms', 'Date pointage', 'Motif', 'Date de création',
];

const useStyles = makeStyles((theme) => ({
  labelPosition: {
    fontWeight: 600,
    marginBottom: theme.spacing(1),
    display: 'block',
    color: theme.palette.text.primary,
    fontSize: '0.875rem',
  },
  filterSection: {
    backgroundColor: '#fafafa',
    borderRadius: theme.spacing(2),
    padding: theme.spacing(3),
    marginBottom: theme.spacing(3),
    border: '1px solid #e0e0e0',
  },
  searchButton: {
    height: '56px',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
    '&:hover': {
      boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
    },
  },
  exportButton: {
    height: '56px',
    fontWeight: 700,
    textTransform: 'none',
    fontSize: '1rem',
    backgroundColor: '#2e7d32',
    '&:hover': {
      backgroundColor: '#1b5e20',
    },
  },
  buttonContainer: {
    display: 'flex',
    gap: theme.spacing(2),
  },
  selectField: {
    backgroundColor: '#ffffff',
  },
  tableHeader: {
    backgroundColor: '#808080',
    '& th': {
      color: '#ffffff',
      fontWeight: 700,
      fontSize: '0.875rem',
    },
  },
  dateField: {
    marginTop: theme.spacing(1),
  },
}));

export default function TimeTrackingHistory(props) {
  const classes = useStyles();
  const directionUser = Number(localStorage.getItem("USER_DIR")) || 1;
  const [openLoadingSimpleBackdrop, setOpenLoadingSimpleBackdrop] = useState(false);
  const [direction, setDirection] = useState([]);
  const [services, setServices] = useState([]);
  const [rows, setRows] = useState([]);
  const [exportLoading, setExportLoading] = useState(false);
  const contextData = useContext(userDetailContext);

  // Pagination
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ✅ On peut changer de direction UNIQUEMENT si USER_DIR === 1
  const canChangeDirection = directionUser === 1;

  const [filters, setFilters] = useState({
    direction_id: directionUser,
    service_id: 0,
    type: "1",
    date_debut: getFirstDayOfMonth(),
    date_fin: getLastDayOfMonth(),
  });

  const handleChanges = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({ ...prev, [name]: value }));
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleSearch = async (customFilters) => {
    const f = customFilters || filters;

    if (!f.date_debut || !f.date_fin) {
      alert("Veuillez sélectionner une date de début et une date de fin");
      return;
    }
    if (new Date(f.date_debut) > new Date(f.date_fin)) {
      alert("La date de début doit être antérieure à la date de fin");
      return;
    }

    setOpenLoadingSimpleBackdrop(true);
    setPage(0);
    try {
      const result = await TimeTrackingService.getlistmanuel(
        f.direction_id,
        f.service_id,
        f.date_debut,
        f.date_fin
      );

      if (result && result.data) {
        setRows(result.data);
      } else {
        setRows([]);
      }
    } catch (error) {
      console.error("Error fetching time tracking data:", error);
      setRows([]);
    } finally {
      setOpenLoadingSimpleBackdrop(false);
    }
  };

  // ✅ Chargement automatique au montage
  useEffect(() => {
    handleSearch({
      direction_id: directionUser,
      service_id: 0,
      date_debut: getFirstDayOfMonth(),
      date_fin: getLastDayOfMonth(),
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const getReportTitle = () => {
    return `SITUATION POINTAGE MANUEL DU ${filters.date_debut} AU ${filters.date_fin}`;
  };

  const getCurrentDate = () => {
    const now = new Date();
    const day = String(now.getDate()).padStart(2, '0');
    const month = monthNames[now.getMonth()];
    const year = now.getFullYear();
    return `Fait à Antananarivo, le ${day} ${month} ${year}`;
  };

  const formatDateWithMonthName = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const day = String(date.getDate()).padStart(2, '0');
    const month = monthNames[date.getMonth()];
    const year = date.getFullYear();
    return `${day} ${month} ${year}`;
  };

  const handleExportExcel = async () => {
    if (rows.length === 0) {
      alert("Aucune donnée à exporter");
      return;
    }

    setExportLoading(true);
    try {
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet('Suivi de présence');

      worksheet.columns = [
        { key: 'index', width: 8 },
        { key: 'direction', width: 20 },
        { key: 'service', width: 20 },
        { key: 'function_name', width: 25 },
        { key: 'full_name', width: 30 },
        { key: 'date_pointage', width: 18 },
        { key: 'observation', width: 35 },
        { key: 'date_creation', width: 18 },
      ];

      worksheet.mergeCells('A1:H2');
      const titleCell = worksheet.getCell('A1');
      titleCell.value = getReportTitle();
      titleCell.font = { bold: true, size: 16, name: 'Arial' };
      titleCell.alignment = { horizontal: 'center', vertical: 'middle' };
      worksheet.getRow(1).height = 25;
      worksheet.getRow(2).height = 25;

      worksheet.getRow(3).height = 10;

      const headerRow = worksheet.getRow(4);
      headerRow.height = 30;
      EXCEL_HEADERS.forEach((label, idx) => {
        const cell = headerRow.getCell(idx + 1);
        cell.value = label;
        cell.fill = {
          type: 'pattern',
          pattern: 'solid',
          fgColor: { argb: 'FF808080' }
        };
        cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 11, name: 'Calibri' };
        cell.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
        cell.border = {
          top: { style: 'thin', color: { argb: 'FF000000' } },
          left: { style: 'thin', color: { argb: 'FF000000' } },
          bottom: { style: 'thin', color: { argb: 'FF000000' } },
          right: { style: 'thin', color: { argb: 'FF000000' } },
        };
      });

      rows.forEach((row, index) => {
        const rowData = worksheet.addRow({
          index: index + 1,
          direction: row.direction_code || '-',
          service: row.service_code || '-',
          function_name: row.function_name || '-',
          full_name: row.nom || '-',
          date_pointage: row.date || '-',
          observation: row.observation || '',
          date_creation: row.date_creation || '-',
        });

        rowData.eachCell((cell) => {
          cell.border = {
            top: { style: 'thin', color: { argb: 'FF000000' } },
            left: { style: 'thin', color: { argb: 'FF000000' } },
            bottom: { style: 'thin', color: { argb: 'FF000000' } },
            right: { style: 'thin', color: { argb: 'FF000000' } },
          };
          cell.alignment = { vertical: 'middle', horizontal: 'center', wrapText: false };
        });
      });

      const footerRow = worksheet.addRow([]);
      footerRow.getCell(5).value = getCurrentDate();
      footerRow.getCell(5).font = { italic: true, size: 10, name: 'Calibri' };
      footerRow.getCell(5).alignment = { horizontal: 'right' };
      worksheet.mergeCells(`E${worksheet.rowCount}:H${worksheet.rowCount}`);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });

      const dateDebutFormatted = formatDateWithMonthName(filters.date_debut);
      const dateFinFormatted = formatDateWithMonthName(filters.date_fin);
      saveAs(blob, `pointage_manuel_${dateDebutFormatted}_au_${dateFinFormatted}.xlsx`);

    } catch (error) {
      console.error("Erreur lors de l'export Excel:", error);
      alert("Une erreur est survenue lors de l'export des données");
    } finally {
      setExportLoading(false);
    }
  };

  useEffect(() => {
    ImmobilisationService.direction().then((result) => {
      if (result && result.data) {
        setDirection(result.data);
      }
    });
  }, []);

  useEffect(() => {
    const fetchServices = async (direction_id) => {
      if (direction_id === 0 || direction_id === "") {
        ServiceService.getService().then((results) => {
          if (results && results.data) {
            setServices(results.data);
          }
        });
      } else {
        PlanningService.Direction(direction_id).then((results) => {
          if (results && results.data) {
            setServices(results.data);
          }
        });
      }
    };
    fetchServices(filters.direction_id);
  }, [filters.direction_id]);

  const paginatedRows = rows.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <MainContainer {...headerData}>
      <LoadingSimpleBackdrop openLoadingSimpleBackdrop={openLoadingSimpleBackdrop} />

      <Box className={classes.filterSection}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <label className={classes.labelPosition}>Direction</label>
            <FormControl fullWidth variant="outlined" size="medium" className={classes.selectField}>
              <Select
                labelId="direction-select-label"
                id="direction-select"
                name="direction_id"
                value={filters.direction_id}
                onChange={handleChanges}
                disabled={!canChangeDirection}
              >
                {canChangeDirection && (
                  <MenuItem value={0}>Toutes les directions</MenuItem>
                )}
                {direction.map((row) => (
                  <MenuItem key={row.direction_id} value={row.direction_id}>
                    {row.name} ({row.code})
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>

          <Grid item xs={12} md={4}>
            <label className={classes.labelPosition}>Service</label>
            <FormControl fullWidth variant="outlined" size="medium" className={classes.selectField}>
              <Select
                labelId="service-select-label"
                id="service-select"
                name="service_id"
                value={filters.service_id}
                onChange={handleChanges}
              >
                <MenuItem value={0}>Tous les services</MenuItem>
                {services.map((row) => (
                  <MenuItem key={row.service_id} value={row.service_id}>
                    {row.code}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
        </Grid>

        <Grid container spacing={3} alignItems="flex-end" style={{ marginTop: 8 }}>
          <Grid item xs={12} md={4}>
            <label className={classes.labelPosition}>Date début</label>
            <TextField
              fullWidth
              type="date"
              name="date_debut"
              value={filters.date_debut}
              onChange={handleChanges}
              variant="outlined"
              size="medium"
              InputLabelProps={{ shrink: true }}
              className={classes.dateField}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <label className={classes.labelPosition}>Date fin</label>
            <TextField
              fullWidth
              type="date"
              name="date_fin"
              value={filters.date_fin}
              onChange={handleChanges}
              variant="outlined"
              size="medium"
              InputLabelProps={{ shrink: true }}
              className={classes.dateField}
            />
          </Grid>

          <Grid item xs={12} md={4}>
            <div className={classes.buttonContainer}>
              <Button
                variant="contained"
                color="primary"
                onClick={() => handleSearch()}
                startIcon={<SearchIconButton />}
                className={classes.searchButton}
                fullWidth
                size="large"
              >
                RECHERCHER
              </Button>
              <Button
                variant="contained"
                onClick={handleExportExcel}
                startIcon={<FileDownloadIcon />}
                className={classes.exportButton}
                fullWidth
                size="large"
                disabled={exportLoading || rows.length === 0}
              >
                {exportLoading ? "EXPORT..." : "EXPORT EXCEL"}
              </Button>
            </div>
          </Grid>
        </Grid>
      </Box>

      <TableContainer component={Paper} elevation={2}>
        <Table sx={{ minWidth: 650 }} aria-label="time tracking table">
          <TableHead className={classes.tableHeader}>
            <TableRow>
              <TableCell align="center" width={60}>N°</TableCell>
              <TableCell>Direction</TableCell>
              <TableCell>Service</TableCell>
              <TableCell>Fonction</TableCell>
              <TableCell>Noms et prénoms</TableCell>
              <TableCell align="center">Date pointage</TableCell>
              <TableCell align="center">Motif</TableCell>
              <TableCell align="center">Date de création</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedRows.length > 0 ? (
              paginatedRows.map((row, index) => (
                <TableRow key={row.id || index} hover sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                  <TableCell align="center">{page * rowsPerPage + index + 1}</TableCell>
                  <TableCell>{row.direction_code || '-'}</TableCell>
                  <TableCell>{row.service_code || '-'}</TableCell>
                  <TableCell>{row.function_name || '-'}</TableCell>
                  <TableCell>{row.nom}</TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} color="primary">
                      {row.date}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} color="primary">
                      {row.observation}
                    </Typography>
                  </TableCell>
                  <TableCell align="center">
                    <Typography variant="body2" fontWeight={600} color="success.main">
                      {row.date_creation}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center" sx={{ py: 8 }}>
                  <AssignmentIcon sx={{ fontSize: 48, color: '#ccc', mb: 2 }} />
                  <Typography variant="h6" color="textSecondary" gutterBottom>
                    Aucune donnée trouvée
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Veuillez modifier vos critères de recherche
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={rows.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Lignes par page :"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} sur ${count !== -1 ? count : `plus de ${to}`}`
          }
        />
      </TableContainer>
    </MainContainer>
  );
}